import PQueue from 'p-queue';
import type { BlobInfo, ICaseDataRepository, ICaseDataRepositoryFactory } from '@/engine/app/CaseDataProvider';
import type { UseCase } from '@/engine/app/interfaces';
import { inject, injectable } from 'inversify';
import type { OfflineDetectionService } from './OfflineDetection.service';
import { TYPES } from '@/engine/ioc';
import { TYPES as OFFLINE_TYPES } from '../ioc/types';
import type { OfflineDB } from './OfflineDB';
import type { CaseInfo } from '@/engine/app/CaseInfoApi';
import type { OfflineUtilsService } from './OfflineUtils.service';
export interface DownloadCaseForOfflineUseCommand {
  caseId: string;
  packageId: string;
}

@injectable()
export class DownloadCaseForOfflineUseCase implements UseCase<DownloadCaseForOfflineUseCommand, Promise<void>> {
  private _caseDataRepository: ICaseDataRepository;

  private _offlineDetectionService: OfflineDetectionService;

  private _offlineDB: OfflineDB;

  private _offlineUtilsService: OfflineUtilsService;

  public constructor(
    @inject(TYPES.ICaseDataRepositoryFactory) caseDataRepositoryFactory: ICaseDataRepositoryFactory,
    @inject(OFFLINE_TYPES.OfflineDetectionService) offlineDetectionService: OfflineDetectionService,
    @inject(OFFLINE_TYPES.OfflineDB) offlineDB: OfflineDB,
    @inject(OFFLINE_TYPES.OfflineUtilsService) offlineUtilsService: OfflineUtilsService
  ) {
    this._caseDataRepository = caseDataRepositoryFactory.create('network');
    this._offlineDetectionService = offlineDetectionService;
    this._offlineDB = offlineDB;
    this._offlineUtilsService = offlineUtilsService;
  }

  public async execute(cmd: DownloadCaseForOfflineUseCommand): Promise<void> {
    if (!this._offlineDetectionService.getOnlineStatus()) {
      throw new Error('Cannot download case for offline while offline');
    }
    this._caseDataRepository.setContext(cmd.caseId, cmd.packageId);
    const caseInfo = await this._caseDataRepository.getCaseInfo();
    const filesList = await this._caseDataRepository.getCaseFilesList();

    const urlResolver = this._caseDataRepository.getCaseBlobUrlResolver();
    const queue = new PQueue({ concurrency: 5 });
    const cachePromise = window.caches.open('case-files').then((cache) => {
      filesList.map((fileInfo) => {
        // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
        queue.add(async () => {
          const response = await fetch(await urlResolver(fileInfo.descriptor));
          return cache.put(
            this._offlineUtilsService.getOfflineUrlForBlob(cmd.packageId, fileInfo.descriptor),
            response
          );
        });
      });
      return queue.onEmpty();
    });
    await cachePromise;
    await this._updateOfflineDb(caseInfo, filesList, cmd.packageId);
  }

  private async _updateOfflineDb(caseInfo: CaseInfo, caseFilesList: Array<BlobInfo>, packageId: string): Promise<void> {
    const currentCaseData = await this._offlineDB.schema.cases.get(caseInfo.caseId);
    if (currentCaseData) {
      const savedPackages = currentCaseData.savedPackages;
      if (!savedPackages.includes(packageId)) {
        await this._offlineDB.schema.cases.update(caseInfo.caseId, {
          savedPackages: [...savedPackages, packageId]
        });
      }
    }
    await this._offlineDB.schema.cases.put({
      ...caseInfo,
      savedPackages: [packageId]
    });

    const packageFiles = caseFilesList.map((fileInfo) => fileInfo.descriptor);
    await this._offlineDB.schema.packageFiles.put({
      packageId,
      files: packageFiles
    });

    const updatePromises = caseFilesList.map((fileInfo) => {
      return this._offlineDB.schema.blobs.put({
        ...fileInfo,
        packageId,
        url: this._offlineUtilsService.getOfflineUrlForBlob(packageId, fileInfo.descriptor),
        expiration: Date.now() + 30 * 24 * 60 * 60 * 1000 // 30 days
      });
    });

    await Promise.all(updatePromises);
  }
}
